import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { combineLatest, EMPTY, Observable, of } from 'rxjs';
import {
  distinctUntilChanged,
  map,
  share,
  shareReplay,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';
import { getJobPageData } from '@collections/jobs';
import { Job, TeamMember } from '@shared/models';
import { getInnerRouterPath } from '@app/app.reducer';
import { getFromCreateJob, getJobFormId } from '@shared/job-form/store';

@Component({
  selector: 'mojo-job-edit-page',
  templateUrl: './job-edit-page.component.html',
  styleUrls: ['./job-edit-page.component.scss'],
})
export class JobEditPageComponent implements OnInit {
  finished = false;
  fromCreateJob$ = this.store.select(getFromCreateJob);
  jobResponse: Partial<Job<TeamMember>>;

  isEdit$ = this.store.select(getInnerRouterPath).pipe(
    map((currentRoute) => currentRoute !== 'create' && currentRoute !== 'clone'),
    distinctUntilChanged(),
  );
  currentRoute$ = this.store.select(getInnerRouterPath).pipe(
    map((currentRoute) => currentRoute),
    distinctUntilChanged(),
  );
  title$ = this.fromCreateJob$.pipe(
    map((routes) =>
      routes !== 'create' && routes !== 'clone'
        ? 'Edit job'
        : routes === 'clone'
        ? 'Clone job'
        : 'Create job',
    ),
  );

  job$: Observable<Partial<Job>> = combineLatest([this.currentRoute$, this.fromCreateJob$]).pipe(
    switchMap(([currentRoute, fromCreateJob]) =>
      fromCreateJob === 'edit' || currentRoute === 'clone'
        ? this.store.select(getJobFormId).pipe(
            switchMap((id: number) => this.store.select(getJobPageData, { id })),
            shareReplay(1),
            tap(() => this.changeDetection.detectChanges()),
          )
        : EMPTY,
    ),
  );

  routerLink$ = combineLatest([this.job$, this.fromCreateJob$]).pipe(
    map(([job, fromCreateJob]) =>
      fromCreateJob === 'clone' || fromCreateJob === 'edit'
        ? `/jobs/job/${job.id}/description`
        : '/',
    ),
    share(),
  );

  constructor(
    private readonly store: Store<{}>,
    private readonly changeDetection: ChangeDetectorRef,
  ) {}
  ngOnInit(): void {
    this.isCurrentRouteClone()
      .pipe(
        switchMap((isCurrentRouteClone) =>
          this.job$.pipe(
            switchMap((job) => {
              if (isCurrentRouteClone) {
                job.clientReqId = null;
              }
              return of(job);
            }),
          ),
        ),
      )
      .subscribe((job) => {
        this.jobResponse = job;
      });
  }

  canDeactivate() {
    return this.finished;
  }

  isCurrentRouteClone() {
    return this.fromCreateJob$.pipe(map((routes) => routes === 'clone'));
  }
}
